import { useState } from 'react';
import {
  Text,
  Group,
  Stack,
  Paper,
  ActionIcon,
  Box,
  Table,
  Tooltip,
} from '@mantine/core';
import {
  IconArrowDown,
} from '@tabler/icons-react';
import TopNav from '../components/TopNav';

interface ComparisonRow {
  feature: string;
  desc: string;
  aldc: { check: boolean; text: string };
  aldcSub?: { check: boolean; text: string };
  standard: { check: boolean; text: string };
}

export default function ApproachPage() {
  const [showTable, setShowTable] = useState(false);

  const comparisonData: ComparisonRow[] = [
    {
      feature: 'Finding Information',
      desc: 'How answers are found',
      aldc: { check: true, text: 'Searches by exact terms AND meaning together' },
      standard: { check: true, text: 'Searches by similarity only' },
    },
    {
      feature: 'Understanding Your Business',
      desc: 'Knowledge of how you operate',
      aldc: { check: true, text: 'Learns your rules, processes, and terminology' },
      standard: { check: false, text: 'Generic responses, no business context' },
    },
    {
      feature: 'Memory & Learning',
      desc: 'Retaining what matters',
      aldc: { check: true, text: 'Remembers decisions and builds on past insights' },
      standard: { check: false, text: 'Starts fresh each conversation' },
    },
    {
      feature: 'Accuracy',
      desc: 'Getting it right',
      aldc: { check: true, text: 'Grounded in your actual data and rules' },
      standard: { check: false, text: 'May make things up (hallucinate)' },
    },
    {
      feature: 'Transparency',
      desc: 'Knowing where answers come from',
      aldc: { check: true, text: 'Shows sources and confidence level' },
      standard: { check: false, text: 'No explanation of reasoning' },
    },
    {
      feature: 'Team Access',
      desc: 'Sharing insights across the organization',
      aldc: { check: true, text: 'Everyone can access shared knowledge' },
      standard: { check: false, text: 'Individual use only' },
    },
    {
      feature: 'Growing With You',
      desc: 'Scalability',
      aldc: { check: true, text: 'Start small, expand as needed' },
      standard: { check: false, text: 'Fixed capability' },
    },
  ];

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      display: 'flex',
      flexDirection: 'column',
      background: '#f8f9fa'
    }}>
      {/* Top Navigation */}
      <TopNav />

      {/* Main Content Area */}
      <div style={{
        flex: 1,
        overflow: 'auto',
        padding: 6
      }}>
        <Paper radius="md" p="xs" style={{ display: 'flex', flexDirection: 'column', minHeight: 'calc(100vh - 72px)' }}>
          {/* Top 48% - Architectures Side by Side */}
          <div style={{ height: '48%', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 30, marginBottom: 4 }}>
            {/* ALDC Approach */}
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <Text ta="center" fw={600} c="gray.7" mb={4} size="sm" style={{ paddingBottom: 4, borderBottom: '2px solid #667eea' }}>
                ALDC Approach
              </Text>
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', padding: 4 }}>
                <Stack align="center" gap={8}>
                  {/* Discovery box around Curated Business Logic */}
                  <Paper p={8} radius="sm" style={{ border: '2px solid #667eea', background: 'rgba(102, 126, 234, 0.05)' }}>
                    <Text size="xs" fw={600} c="#667eea" ta="center" mb={6}>DISCOVERY</Text>
                    <Tooltip
                      multiline
                      w={420}
                      withArrow
                      label={
                        <div style={{ fontSize: '13px', lineHeight: '1.5' }}>
                          <div style={{ marginBottom: '3px' }}>• We learn how your business actually works</div>
                          <div style={{ marginBottom: '3px' }}>• Capture your rules, processes, and decisions</div>
                          <div>• Focus on what matters, not everything</div>
                        </div>
                      }
                    >
                      <Paper
                        p={10}
                        radius="sm"
                        style={{
                          width: 180,
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          color: 'white',
                          textAlign: 'center',
                          cursor: 'pointer'
                        }}
                      >
                        <Text size="xs" fw={600}>YOUR BUSINESS RULES</Text>
                      </Paper>
                    </Tooltip>
                  </Paper>

                  <Box style={{ width: 0, height: 0, borderLeft: '6px solid transparent', borderRight: '6px solid transparent', borderTop: '8px solid #667eea', opacity: 0.5 }} />

                  {/* Eclipse with Unstructured Data on same line */}
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center', justifyContent: 'center' }}>
                    <Tooltip
                      multiline
                      w={420}
                      withArrow
                      label={
                        <div style={{ fontSize: '13px', lineHeight: '1.5' }}>
                          <div style={{ marginBottom: '3px' }}>• Makes your business knowledge usable by AI</div>
                          <div style={{ marginBottom: '3px' }}>• Connects to your existing systems</div>
                          <div>• Creates a layer your team can actually use</div>
                        </div>
                      }
                    >
                      <Paper
                        p={10}
                        radius="sm"
                        style={{
                          width: 100,
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          color: 'white',
                          textAlign: 'center',
                          cursor: 'pointer'
                        }}
                      >
                        <Text size="xs" fw={600}>ECLIPSE</Text>
                      </Paper>
                    </Tooltip>
                    <Tooltip
                      multiline
                      w={420}
                      withArrow
                      label={
                        <div style={{ fontSize: '13px', lineHeight: '1.5' }}>
                          <div style={{ marginBottom: '3px' }}>• PDFs, documents, emails, reports</div>
                          <div style={{ marginBottom: '3px' }}>• All your existing files and content</div>
                          <div>• Works alongside your business rules</div>
                        </div>
                      }
                    >
                      <Paper
                        p={10}
                        radius="sm"
                        style={{
                          width: 110,
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          color: 'white',
                          textAlign: 'center',
                          cursor: 'pointer'
                        }}
                      >
                        <Text size="xs" fw={600}>YOUR DOCUMENTS</Text>
                      </Paper>
                    </Tooltip>
                  </div>

                  <Box style={{ width: 0, height: 0, borderLeft: '6px solid transparent', borderRight: '6px solid transparent', borderTop: '8px solid #667eea', opacity: 0.5 }} />

                  {/* Zeus Memory on its own level */}
                  <Tooltip
                    multiline
                    w={500}
                    withArrow
                    label={
                      <div style={{ fontSize: '13px', lineHeight: '1.5' }}>
                        <div style={{ fontWeight: 600, marginBottom: '8px' }}>How We Find Information:</div>
                        <div style={{ marginBottom: '6px' }}>• Exact Match - Finds specific terms you use</div>
                        <div style={{ marginBottom: '6px' }}>• Meaning Match - Understands related concepts</div>
                        <div style={{ marginBottom: '10px' }}>• Smart Ranking - Combines both for best results</div>

                        <div style={{ fontWeight: 600, marginBottom: '8px' }}>What Makes It Smart:</div>
                        <div style={{ marginBottom: '6px' }}>• Detects patterns in your data automatically</div>
                        <div style={{ marginBottom: '6px' }}>• Responds instantly (under 10ms)</div>
                        <div style={{ marginBottom: '10px' }}>• Remembers context and past decisions</div>

                        <div style={{ fontWeight: 600, marginBottom: '8px' }}>The Result:</div>
                        <div>12-22% more accurate answers because we search by both exact terms AND meaning, not just one or the other.</div>
                      </div>
                    }
                  >
                    <Paper
                      p={10}
                      radius="sm"
                      style={{
                        width: 200,
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white',
                        textAlign: 'center',
                        cursor: 'pointer'
                      }}
                    >
                      <Text size="xs" fw={600}>SMART MEMORY</Text>
                    </Paper>
                  </Tooltip>

                  <Box style={{ width: 0, height: 0, borderLeft: '6px solid transparent', borderRight: '6px solid transparent', borderTop: '8px solid #667eea', opacity: 0.5 }} />

                  <Tooltip
                    multiline
                    w={500}
                    withArrow
                    label={
                      <div style={{ fontSize: '13px', lineHeight: '1.5' }}>
                        <div style={{ marginBottom: '3px' }}>• Dual search architecture: semantic (business logic) + vector (similarity)</div>
                        <div style={{ marginBottom: '3px' }}>• Intelligent routing to optimal LLM (local or frontier models)</div>
                        <div style={{ marginBottom: '3px' }}>• Context-aware tool selection for each specific task</div>
                        <div style={{ marginBottom: '3px' }}>• Generates specialized outputs: dashboards, reports, portfolio analysis</div>
                        <div>• Maintains full organizational control and governance</div>
                      </div>
                    }
                  >
                    <Paper
                      p={10}
                      radius="sm"
                      style={{
                        width: 180,
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white',
                        textAlign: 'center',
                        cursor: 'pointer'
                      }}
                    >
                      <Text size="xs" fw={600} lh={1.2}>INTELLIGENT SEARCH & GENERATION</Text>
                    </Paper>
                  </Tooltip>
                </Stack>
              </div>
              <div style={{ padding: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Box style={{ width: '90%', height: 2, background: '#4a5568', marginBottom: 4 }} />
                <Text fw={700} size="xs" c="#667eea">Enterprise Intelligence Asset</Text>
              </div>
            </div>

            {/* Typical AI Chat Deployment */}
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <Text ta="center" fw={600} c="gray.7" mb={4} size="sm" style={{ paddingBottom: 4, borderBottom: '2px solid #48bb78' }}>
                Typical AI Chat Deployment
              </Text>
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', padding: 4 }}>
                <Stack align="center" gap={8}>
                  <Tooltip
                    multiline
                    w={380}
                    withArrow
                    label={
                      <div style={{ fontSize: '13px', lineHeight: '1.5' }}>
                        <div style={{ marginBottom: '3px' }}>• Statistical vector representations</div>
                        <div>• Converts text to numbers for similarity matching</div>
                      </div>
                    }
                  >
                    <Paper
                      p={10}
                      radius="sm"
                      style={{
                        width: 180,
                        background: 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)',
                        color: 'white',
                        textAlign: 'center',
                        cursor: 'pointer'
                      }}
                    >
                      <Text size="xs" fw={600}>EMBEDDINGS</Text>
                    </Paper>
                  </Tooltip>
                  <Box style={{ width: 0, height: 0, borderLeft: '6px solid transparent', borderRight: '6px solid transparent', borderTop: '8px solid #48bb78', opacity: 0.5 }} />
                  <Tooltip
                    multiline
                    w={380}
                    withArrow
                    label={
                      <div style={{ fontSize: '13px', lineHeight: '1.5' }}>
                        <div style={{ marginBottom: '3px' }}>• Finds 'similar' content based on mathematical distance</div>
                        <div>• Not semantic understanding</div>
                      </div>
                    }
                  >
                    <Paper
                      p={10}
                      radius="sm"
                      style={{
                        width: 180,
                        background: 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)',
                        color: 'white',
                        textAlign: 'center',
                        cursor: 'pointer'
                      }}
                    >
                      <Text size="xs" fw={600}>VECTOR SEARCH</Text>
                    </Paper>
                  </Tooltip>
                  <Box style={{ width: 0, height: 0, borderLeft: '6px solid transparent', borderRight: '6px solid transparent', borderTop: '8px solid #48bb78', opacity: 0.5 }} />
                  <Tooltip
                    multiline
                    w={380}
                    withArrow
                    label={
                      <div style={{ fontSize: '13px', lineHeight: '1.5' }}>
                        <div style={{ marginBottom: '3px' }}>• Injects retrieved context into prompts</div>
                        <div>• Hopes the model figures out what's relevant</div>
                      </div>
                    }
                  >
                    <Paper
                      p={10}
                      radius="sm"
                      style={{
                        width: 180,
                        background: 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)',
                        color: 'white',
                        textAlign: 'center',
                        cursor: 'pointer'
                      }}
                    >
                      <Text size="xs" fw={600}>RAG AUGMENTATION</Text>
                    </Paper>
                  </Tooltip>
                  <Box style={{ width: 0, height: 0, borderLeft: '6px solid transparent', borderRight: '6px solid transparent', borderTop: '8px solid #48bb78', opacity: 0.5 }} />
                  <Tooltip
                    multiline
                    w={320}
                    withArrow
                    label={
                      <div style={{ fontSize: '13px', lineHeight: '1.5' }}>
                        <div style={{ marginBottom: '3px' }}>• Adjusts model weights through training</div>
                        <div>• Expensive, still probabilistic</div>
                      </div>
                    }
                  >
                    <Paper
                      p={10}
                      radius="sm"
                      style={{
                        width: 180,
                        background: 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)',
                        color: 'white',
                        textAlign: 'center',
                        cursor: 'pointer'
                      }}
                    >
                      <Text size="xs" fw={600}>FINE-TUNING</Text>
                    </Paper>
                  </Tooltip>
                  <Box style={{ width: 0, height: 0, borderLeft: '6px solid transparent', borderRight: '6px solid transparent', borderTop: '8px solid #48bb78', opacity: 0.5 }} />
                  <Tooltip
                    multiline
                    w={320}
                    withArrow
                    label={
                      <div style={{ fontSize: '13px', lineHeight: '1.5' }}>
                        <div style={{ marginBottom: '3px' }}>• Probabilistic output</div>
                        <div style={{ marginBottom: '3px' }}>• Best guess based on patterns</div>
                        <div>• Prone to hallucinations</div>
                      </div>
                    }
                  >
                    <Paper
                      p={10}
                      radius="sm"
                      style={{
                        width: 180,
                        background: 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)',
                        color: 'white',
                        textAlign: 'center',
                        cursor: 'pointer'
                      }}
                    >
                      <Text size="xs" fw={600} lh={1.1}>GENERATION</Text>
                    </Paper>
                  </Tooltip>
                </Stack>
              </div>
              <div style={{ padding: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Box style={{ width: '90%', height: 2, background: '#4a5568', marginBottom: 4 }} />
                <Text fw={700} size="xs" c="#48bb78">Single-Purpose AI Tool</Text>
              </div>
            </div>
          </div>

          {/* Bottom - Expandable Comparison Table */}
          <div style={{ flex: 1, borderTop: '1px solid #e2e8f0', paddingTop: 4, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            {/* Toggle Button */}
            <div style={{ padding: '8px 16px', display: 'flex', alignItems: 'center', gap: 8 }}>
              <ActionIcon
                variant="subtle"
                color="gray"
                onClick={() => setShowTable(!showTable)}
                style={{
                  transform: showTable ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.3s ease'
                }}
              >
                <IconArrowDown size={20} />
              </ActionIcon>
            </div>

            {/* Table - Conditional Rendering */}
            {showTable && (
              <Paper radius="md" style={{ background: '#f7fafc', flex: 1, overflow: 'auto' }}>
                <Table horizontalSpacing="sm" verticalSpacing="xs" style={{ fontSize: '0.75rem' }}>
                  <Table.Thead>
                    <Table.Tr style={{ background: '#2d3748' }}>
                      <Table.Th style={{ color: 'white', fontWeight: 600, width: '25%' }}>Capability</Table.Th>
                      <Table.Th style={{ color: 'white', fontWeight: 600, width: '37.5%' }}>ALDC Approach</Table.Th>
                      <Table.Th style={{ color: 'white', fontWeight: 600, width: '37.5%' }}>Typical AI Chat Deployment</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {comparisonData.map((row, index) => (
                      <Table.Tr key={index}>
                        <Table.Td style={{ background: '#f7fafc' }}>
                          <Text fw={600} size="xs">{row.feature}</Text>
                          <Text size="xs" c="dimmed">{row.desc}</Text>
                        </Table.Td>
                        <Table.Td style={{ background: 'rgba(102, 126, 234, 0.03)' }}>
                          {row.aldc.text.includes('\n') ? (
                            <div>
                              {row.aldc.text.split('\n').map((line: string, i: number) => (
                                <Group key={i} gap={8} style={{ marginBottom: i < row.aldc.text.split('\n').length - 1 ? 2 : 0 }}>
                                  <Text c={row.aldc.check ? 'green' : 'red'} fw={700}>{row.aldc.check ? '✓' : '✗'}</Text>
                                  <Text size="xs">{line}</Text>
                                </Group>
                              ))}
                            </div>
                          ) : row.aldcSub ? (
                            <div style={{ display: 'flex', gap: 8 }}>
                              <div style={{ flex: 1 }}>
                                <Group gap={8}>
                                  <Text c={row.aldc.check ? 'green' : 'red'} fw={700}>{row.aldc.check ? '✓' : '✗'}</Text>
                                  <Text size="xs">{row.aldc.text}</Text>
                                </Group>
                              </div>
                              <div style={{ flex: 1 }}>
                                {row.aldcSub?.text.split('\n').map((line: string, i: number) => (
                                  <Group key={i} gap={8} style={{ marginBottom: i < (row.aldcSub?.text.split('\n').length ?? 0) - 1 ? 2 : 0 }}>
                                    <Text c={row.aldcSub?.check ? 'green' : 'red'} fw={700}>{row.aldcSub?.check ? '✓' : '✗'}</Text>
                                    <Text size="xs">{line}</Text>
                                  </Group>
                                ))}
                              </div>
                            </div>
                          ) : (
                            <Group gap={8}>
                              <Text c={row.aldc.check ? 'green' : 'red'} fw={700}>{row.aldc.check ? '✓' : '✗'}</Text>
                              <Text size="xs">{row.aldc.text}</Text>
                            </Group>
                          )}
                        </Table.Td>
                        <Table.Td style={{ background: 'rgba(72, 187, 120, 0.03)' }}>
                          <Group gap={8}>
                            <Text c={row.standard.check ? 'green' : 'red'} fw={700}>{row.standard.check ? '✓' : '✗'}</Text>
                            <Text size="xs">{row.standard.text}</Text>
                          </Group>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Paper>
            )}
          </div>
        </Paper>
      </div>
    </div>
  );
}
